import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import AdminLayout from './components/layout/AdminLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

import UsersPage from './pages/users/UsersPage';
import UserDetailsPage from './pages/users/UserDetailsPage';

import AstrologersPage from './pages/astrologers/AstrologersPage';
import AstrologerDetailsPage from './pages/astrologers/AstrologerDetailsPage';
import AstrologerApplicationsPage from './pages/astrologers/AstrologerApplicationsPage';
import AstrologerApplicationDetailsPage from './pages/astrologers/AstrologerApplicationDetailsPage';
import KycReviewPage from './pages/astrologers/KycReviewPage';

import ConsultationsPage from './pages/consultations/ConsultationsPage';
import ConsultationDetailsPage from './pages/consultations/ConsultationDetailsPage';
import AppointmentsPage from './pages/consultations/AppointmentsPage';
import EmergencyPage from './pages/consultations/EmergencyPage';

import CategoriesPage from './pages/content/CategoriesPage';
import BannersPage from './pages/content/BannersPage';
import FAQsPage from './pages/content/FAQsPage';
import LanguagesPage from './pages/content/LanguagesPage';
import TranslationsPage from './pages/content/TranslationsPage';
import OffersPage from './pages/content/OffersPage';
import FeatureSettingsPage from './pages/settings/FeatureSettingsPage';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import ActivityLogsPage from './pages/settings/ActivityLogsPage';

import PaymentsPage from './pages/finance/PaymentsPage';
import PaymentGatewaysPage from './pages/finance/PaymentGatewaysPage';
import WithdrawalsPage from './pages/finance/WithdrawalsPage';
import CommissionPage from './pages/finance/CommissionPage';
import TransactionsPage from './pages/finance/TransactionsPage';
import RechargePackagesPage from './pages/finance/RechargePackagesPage';
import RevenuePage from './pages/finance/RevenuePage';

import ReviewsPage from './pages/reviews/ReviewsPage';
import CmsPagesPage from './pages/cms/CmsPagesPage';
import { CeoProfilePage, TarotByVrindaPage } from './pages/cms/CmsProfilePage';

import ReportsPage from './pages/reports/ReportsPage';
import SupportPage, { SupportTicketPage } from './pages/support/SupportPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import SettingsPage from './pages/settings/SettingsPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000, refetchOnWindowFocus: false } },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 3500,
            className: 'text-sm',
            style: {
              maxWidth: 380,
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid #EFEAE4',
            },
            success: { duration: 3500 },
            error: { duration: 4500 },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<AdminLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />

            <Route path="users" element={<UsersPage />} />
            <Route path="users/:id" element={<UserDetailsPage />} />

            <Route path="astrologers" element={<AstrologersPage />} />
            <Route path="astrologers/applications" element={<AstrologerApplicationsPage />} />
            <Route path="astrologers/applications/:id" element={<AstrologerApplicationDetailsPage />} />
            <Route path="astrologers/kyc" element={<KycReviewPage />} />
            <Route path="astrologers/:id" element={<AstrologerDetailsPage />} />

            <Route path="consultations" element={<ConsultationsPage />} />
            <Route path="consultations/appointments" element={<AppointmentsPage />} />
            <Route path="consultations/emergency" element={<EmergencyPage />} />
            <Route path="consultations/:id" element={<ConsultationDetailsPage />} />

            <Route path="support" element={<SupportPage />} />
            <Route path="support/:id" element={<SupportTicketPage />} />

            <Route path="finance/payments" element={<PaymentsPage />} />
            <Route path="finance/transactions" element={<TransactionsPage />} />
            <Route path="finance/revenue" element={<RevenuePage />} />
            <Route path="finance/withdrawals" element={<WithdrawalsPage />} />
            <Route path="finance/commission" element={<CommissionPage />} />
            <Route path="finance/gateways" element={<PaymentGatewaysPage />} />
            <Route path="finance/packages" element={<RechargePackagesPage />} />

            <Route path="reviews" element={<ReviewsPage />} />

            <Route path="content/banners" element={<BannersPage />} />
            <Route path="content/categories" element={<CategoriesPage />} />
            <Route path="content/faqs" element={<FAQsPage />} />
            <Route path="cms/pages" element={<CmsPagesPage />} />
            <Route path="cms/ceo-profile" element={<CeoProfilePage />} />
            <Route path="cms/tarot-by-vrinda" element={<TarotByVrindaPage />} />

            <Route path="language/languages" element={<LanguagesPage />} />
            <Route path="language/translations" element={<TranslationsPage />} />

            <Route path="offers" element={<OffersPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />

            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/features" element={<FeatureSettingsPage />} />
            <Route path="settings/integrations" element={<IntegrationsPage />} />
            <Route path="settings/activity-logs" element={<ActivityLogsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
